import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, Info, Lightbulb, OctagonAlert } from 'lucide-react';
import type {
  SmartInsight,
  SmartInsightParam,
  SmartReportRequest,
  SmartReportResult,
  SmartValue,
} from '@optiflow/types';
import { Card } from '@/components/ui/Card';
import { formatMoney } from '@/modules/catalog/money';
import { useLocale } from '@/hooks/useLocale';
import { cn } from '@/utils/cn';
import { ChangeText } from '../components/ChangeText';
import { ExportCsvButton } from '../components/ExportCsvButton';
import { ReportMetaLine } from '../components/ReportMetaLine';
import { SimpleTable } from '../components/ReportBlocks';
import { SummaryChips } from './SmartBuilder';
import type { SmartSummaryChip } from './smartSummary';
import type { SmartStep } from './smartDraft';
import { SmartValueText } from './SmartValueText';
import {
  areaLabel,
  branchStateLabel,
  codeLabel,
  dimensionLabel,
  metricLabel,
  rowLabel,
} from './smartLabels';

const SmartChart = lazy(() => import('./SmartChart'));
const LOCALE_TAG = { en: 'en-GB', ar: 'ar-EG' } as const;

/**
 * A generated Smart Report, in the order the reader needs it: what was asked, the figure, its
 * chart and table, what the rules noticed, the way into the full tab, and what to ask next. There
 * is no AI summary: every sentence is a fixed template filled with numbers copied from the result.
 */
export function SmartResultView({
  result,
  chips,
  onEdit,
  onSuggestion,
}: {
  result: SmartReportResult;
  chips: SmartSummaryChip[];
  onEdit: (step: SmartStep) => void;
  onSuggestion: (request: SmartReportRequest) => void;
}) {
  const { t } = useTranslation();
  const title = metricLabel(t, result.metric);
  const figure = result.figure;
  const moneyChange = figure.moneyChange;

  return (
    <div className="space-y-5">
      <section aria-labelledby="smart-result-title" className="space-y-3">
        <div className="space-y-2">
          <p className="text-brand-700 text-xs font-semibold tracking-wide uppercase">
            {areaLabel(t, result.metric.area)}
          </p>
          <h2 id="smart-result-title" className="text-ink text-xl font-semibold text-balance">
            {title}
          </h2>
          <p id="smart-built-from" className="text-muted text-xs">
            {t('reports.smart.builtFrom')}
          </p>
          <SummaryChips chips={chips} onEdit={onEdit} labelledBy="smart-built-from" />
          {result.metric.asOfNow ? (
            <p className="text-muted text-xs">{t('reports.smart.asOfNowNote')}</p>
          ) : (
            <ReportMetaLine meta={result.meta} />
          )}
          <ExportCsvButton
            target={{ method: 'POST', path: '/reports/smart/export', body: result.request }}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card className="min-w-0 p-4">
            <p className="text-muted text-sm">
              {result.metric.asOfNow ? t('reports.asOfNow') : t('reports.smart.thisPeriod')}
            </p>
            <div className="text-ink mt-2 text-2xl font-semibold">
              <SmartValueText value={figure.current} />
            </div>
            {figure.countChange ? (
              <div className="mt-1">
                <ChangeText basisPoints={figure.countChange.pctBasisPoints} />
              </div>
            ) : null}
            {moneyChange ? (
              <ul className="mt-1 space-y-0.5">
                {moneyChange.map((entry) => (
                  <li key={entry.currencyCode} className="flex items-center gap-2 text-xs">
                    <span dir="ltr" className="text-muted">
                      {entry.currencyCode}
                    </span>
                    <ChangeText basisPoints={entry.pctBasisPoints} />
                  </li>
                ))}
              </ul>
            ) : null}
          </Card>
          {figure.previous ? (
            <Card className="min-w-0 p-4">
              <p className="text-muted text-sm">{t('reports.smart.previousPeriod')}</p>
              <div className="text-ink mt-2 text-2xl font-semibold">
                <SmartValueText value={figure.previous} />
              </div>
            </Card>
          ) : null}
        </div>
      </section>

      {result.trend || result.breakdown ? (
        <Card className="min-w-0 space-y-3 p-4">
          <h3 className="text-ink font-semibold">
            {result.breakdown
              ? t('reports.smart.byDimension', {
                  dimension: dimensionLabel(t, result.breakdown.dimension, result.metric),
                })
              : t('reports.smart.overTime')}
          </h3>
          <Suspense fallback={null}>
            <SmartChart result={result} title={title} />
          </Suspense>
          <ResultTable result={result} />
        </Card>
      ) : null}

      {result.insights.length > 0 ? (
        <section aria-labelledby="smart-insights" className="space-y-2">
          <h3 id="smart-insights" className="text-ink flex items-center gap-2 font-semibold">
            <Lightbulb className="text-brand-600 size-4" aria-hidden />
            {t('reports.smart.insightsTitle')}
          </h3>
          <ul className="space-y-2">
            {result.insights.map((insight, index) => (
              <InsightLine key={`${insight.code}-${index}`} insight={insight} result={result} />
            ))}
          </ul>
          <p className="text-muted text-xs">{t('reports.smart.insightsNote')}</p>
        </section>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Link
          to={{
            pathname: result.tab.path,
            search: new URLSearchParams(result.tab.params).toString(),
          }}
          className="text-brand-700 inline-flex items-center gap-1 text-sm font-medium hover:underline"
        >
          {t('reports.smart.openTab', { area: areaLabel(t, result.metric.area) })}
          <ArrowUpRight className="size-4 rtl:-scale-x-100" aria-hidden />
        </Link>
      </div>

      {result.suggestions.length > 0 ? (
        <section aria-labelledby="smart-next" className="space-y-2">
          <h3 id="smart-next" className="text-ink font-semibold">
            {t('reports.smart.nextTitle')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {result.suggestions.map((suggestion) => (
              <button
                key={suggestion.key}
                type="button"
                onClick={() => onSuggestion(suggestion.request)}
                className="border-line text-ink hover:border-brand-300 rounded-full border bg-white px-3 py-1.5 text-sm"
              >
                {t(`reports.smart.suggestions.${suggestion.key}`)}
              </button>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function ResultTable({ result }: { result: SmartReportResult }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const breakdown = result.breakdown;
  if (breakdown) {
    const withState = [
      'WHATSAPP_HEALTH',
      'WHATSAPP_AUTOMATION_UNAVAILABLE',
      'AUTOMATION_RULES',
    ].includes(result.metric.code);
    if (breakdown.rows.length === 0)
      return <p className="text-muted text-sm">{t('reports.smart.noRows')}</p>;
    return (
      <SimpleTable
        fluid
        caption={t('reports.smart.byDimension', {
          dimension: dimensionLabel(t, breakdown.dimension, result.metric),
        })}
        headers={[
          dimensionLabel(t, breakdown.dimension, result.metric),
          withState ? t('reports.smart.state') : metricLabel(t, result.metric),
        ]}
        ends={withState ? [] : [1]}
        rows={breakdown.rows.map((row, index) => ({
          key: `${row.id ?? row.code ?? 'none'}-${index}`,
          cells: [
            <span key="label" className="break-words">
              {rowLabel(t, locale, breakdown.dimension, row)}
              {row.code && breakdown.dimension === 'PRODUCT' ? (
                <span dir="ltr" className="text-muted ms-1 text-xs">
                  {row.code}
                </span>
              ) : null}
            </span>,
            withState ? (
              <span key="value">
                {yesNo(t, row.value)} · {branchStateLabel(t, result.metric, row.code)}
              </span>
            ) : (
              <SmartValueText key="value" value={row.value} />
            ),
          ],
        }))}
      />
    );
  }
  const trend = result.trend;
  if (!trend) return null;
  return (
    <SimpleTable
      fluid
      caption={t('reports.smart.overTime')}
      headers={[
        t('reports.smart.bucket'),
        ...trend.series.map((series) => seriesLabel(t, series.key, result)),
      ]}
      ends={trend.series.map((_, index) => index + 1)}
      rows={trend.buckets.map((bucket, index) => ({
        key: bucket.from,
        cells: [
          <span key="bucket" dir="ltr">
            {bucket.from === bucket.to ? bucket.from : `${bucket.from} – ${bucket.to}`}
          </span>,
          ...trend.series.map((series) => {
            const value = series.values[index] ?? null;
            if (value === null) return '—';
            return result.metric.unit === 'MONEY' ? (
              <span key={series.key} dir="ltr">
                {formatMoney(value, locale, series.key)}
              </span>
            ) : (
              new Intl.NumberFormat(LOCALE_TAG[locale]).format(value)
            );
          }),
        ],
      }))}
    />
  );
}

function yesNo(t: ReturnType<typeof useTranslation>['t'], value: SmartValue): string {
  return value.kind === 'COUNT' && value.value > 0 ? t('reports.crm.yes') : t('reports.crm.no');
}

function seriesLabel(
  t: ReturnType<typeof useTranslation>['t'],
  key: string,
  result: SmartReportResult,
): string {
  if (key === 'AUTOMATED') return t('reports.crm.mode.AUTOMATED_PROVIDER');
  if (key === 'MANUAL') return t('reports.crm.mode.MANUAL_HANDOFF');
  if (key === 'COUNT') return metricLabel(t, result.metric);
  return key;
}

const SEVERITY_ICON = {
  INFO: Info,
  POSITIVE: Info,
  WARNING: AlertTriangle,
  CRITICAL: OctagonAlert,
} as const;

/** One insight: a fixed sentence per code, its numbers copied from the result and formatted for the reader. */
function InsightLine({ insight, result }: { insight: SmartInsight; result: SmartReportResult }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const Icon = SEVERITY_ICON[insight.severity];
  const values = Object.fromEntries(
    Object.entries(insight.params).map(([name, param]) => [name, paramText(t, locale, param)]),
  );
  return (
    <li
      className={cn(
        'flex items-start gap-2 rounded-lg border px-3 py-2 text-sm',
        insight.severity === 'CRITICAL'
          ? 'border-red-200 bg-red-50 text-red-900'
          : insight.severity === 'WARNING'
            ? 'border-amber-200 bg-amber-50 text-amber-900'
            : 'border-line text-ink bg-white',
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span className="min-w-0 break-words">
        {t(
          `reports.smart.insights.${insight.code === 'TOP_CONTRIBUTOR' && 'share' in insight.params ? 'TOP_CONTRIBUTOR_SHARE' : insight.code}`,
          {
            ...values,
            metric: metricLabel(t, result.metric),
            interpolation: { escapeValue: false },
          },
        )}
      </span>
    </li>
  );
}

function paramText(
  t: ReturnType<typeof useTranslation>['t'],
  locale: 'en' | 'ar',
  param: SmartInsightParam,
): string {
  const number = new Intl.NumberFormat(LOCALE_TAG[locale], { maximumFractionDigits: 1 });
  switch (param.kind) {
    case 'MONEY':
      return formatMoney(param.amountMinor, locale, param.currencyCode);
    case 'COUNT':
      return number.format(param.value);
    case 'PERCENT':
      return `${number.format(param.basisPoints / 100)}%`;
    case 'DURATION':
      return t('reports.smart.days', { days: number.format(param.seconds / 86_400) });
    case 'ENTITY':
      return (
        (locale === 'ar' ? (param.labelAr ?? param.label) : (param.label ?? param.labelAr)) ??
        t('reports.smart.unattributed')
      );
    case 'CODE':
      if (param.dimension === 'DUE_STATE') return t(`reports.smart.dueState.${param.code}`);
      if (param.code === 'UNATTRIBUTED') return t('reports.smart.unattributed');
      if (param.code === 'NO_REGISTRATION_BRANCH')
        return t('reports.customers.noRegistrationBranch');
      return codeLabel(t, param.dimension, param.code);
  }
}
